import type { RefCallback } from 'react';
import {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import type { CarouselAPI } from '@elfsight/lib-carousel';
import { Show } from '@elfsight/lib-utils';
import type { LayoutAPI } from '@elfsight/widget-ui';
import {
  CarouselLayout,
  isTransparentBackground,
  mapCarouselLayoutSettings,
  useDebounceCallback
} from '@elfsight/widget-ui';
import { useData } from '../../data';
import { usePopup } from '../../popup';
import { useSocialFeedSettings } from '../../utils';
import { CarouselLoader } from '../CarouselLoader';
import { useLayout } from '../LayoutProvider';
import { renderLoader, renderPost } from '../utils';

type AutoplayControlsType = {
  start: (() => void) | undefined;
  stop: (() => void) | undefined;
};

const DEBOUNCE_CALLBACK_DELAY = 350;

export const Carousel = forwardRef<LayoutAPI & CarouselAPI, unknown>(
  (_props, forwardingRef) => {
    const settings = useSocialFeedSettings();
    const {
      postHeightNormalization,
      renderThumbnailMode,
      carouselAutoplay,
      postTextColor,
      widgetBackground,
      widgetHorizontalPadding
    } = settings;
    const dataSource = useData();
    const { isOpen } = usePopup();
    const { setRerender, setCarouselVisibleIndexes, carouselVisibleIndexes } =
      useLayout();
    const carouselRowsRef = useRef(0);

    const isHeightNormalizationAvailable =
      postHeightNormalization &&
      carouselVisibleIndexes.length > 1 &&
      carouselRowsRef.current === 1;

    const [isLayoutTransitioning, setIsLayoutTransitioning] = useState(true);

    const applyHeightNormalization =
      isHeightNormalizationAvailable && !isLayoutTransitioning;

    const carouselSettings = mapCarouselLayoutSettings(settings);

    const [autoplay, setAutoplay] = useState(carouselAutoplay);
    const autoplayIsRunningRef = useRef(false);
    const [autoplayControls, setAutoplayControls] =
      useState<AutoplayControlsType>({
        start: undefined,
        stop: undefined
      });

    const carouselCallbackRef: RefCallback<CarouselAPI & LayoutAPI> =
      useCallback(
        (node) => {
          if (!node) {
            return;
          }

          setRerender(node.updateAutoHeight);
          setAutoplayControls({
            start: () => setAutoplay(true),
            stop: () => setAutoplay(false)
          });

          if (forwardingRef && 'current' in forwardingRef) {
            forwardingRef.current = node;
          }
        },
        [forwardingRef, setRerender]
      );

    // "Костыль", чтоб избежать конфликта с pauseOnHover запуска/остановки карусели
    const debounceAutoplayStop = useDebounceCallback(() => {
      autoplayControls.stop?.();
    }, DEBOUNCE_CALLBACK_DELAY);
    const debounceAutoplayStart = useDebounceCallback(
      () => autoplayControls.start?.(),
      DEBOUNCE_CALLBACK_DELAY
    );

    useEffect(() => {
      const timer = setTimeout(() => setIsLayoutTransitioning(false), 200);

      return () => clearTimeout(timer);
    }, [isLayoutTransitioning]);

    useEffect(() => {
      const shouldStartAutoplay =
        carouselAutoplay && !isOpen && !renderThumbnailMode;

      if (shouldStartAutoplay && !autoplayIsRunningRef.current) {
        debounceAutoplayStart();
        autoplayIsRunningRef.current = true;
      }

      if (!shouldStartAutoplay && autoplayIsRunningRef.current) {
        debounceAutoplayStop();
        autoplayIsRunningRef.current = false;
      }
    }, [
      isOpen,
      carouselAutoplay,
      debounceAutoplayStart,
      debounceAutoplayStop,
      renderThumbnailMode
    ]);

    return (
      <Suspense fallback={null}>
        <Show when={isHeightNormalizationAvailable && isLayoutTransitioning}>
          <StyledCarouselLoader />
        </Show>

        <StyledCarousel
          {...carouselSettings}
          autoplay={autoplay}
          ref={carouselCallbackRef}
          dataSource={dataSource}
          onVisibleItemsChange={setCarouselVisibleIndexes}
          // @ts-expect-error unknown не может применится для типа DataValue
          renderItem={renderPost}
          renderLoader={renderLoader(postTextColor)}
          reportSlideChangeInstantly={
            carouselSettings.swipeDesktopEnable ||
            carouselSettings.swipeMobileEnable
          }
          containerHorizontalPadding={
            isTransparentBackground(widgetBackground)
              ? undefined
              : widgetHorizontalPadding
          }
          onRowsChange={(rowsCount) => {
            carouselRowsRef.current = rowsCount;
          }}
          onColumnsChange={() => {
            setIsLayoutTransitioning(true);
          }}
          _isLayoutTransitioning={isLayoutTransitioning}
          _heightNormalization={applyHeightNormalization}
        />
      </Suspense>
    );
  }
);

const StyledCarouselLoader = styled(CarouselLoader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledCarousel = styled(CarouselLayout)<{
  _heightNormalization?: boolean;
  _isLayoutTransitioning?: boolean;
}>`
  ${({ _isLayoutTransitioning }) =>
    _isLayoutTransitioning &&
    css`
      &,
      & * {
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `};

  ${({ _heightNormalization }) =>
    // Выравнивание фона карточек под один уровень
    _heightNormalization &&
    css`
      & .swiper-wrapper {
        height: 100% !important;
        align-items: stretch !important;
      }

      & .es-carousel-layout-item,
      & .es-carousel-layout-item > div {
        height: 100% !important;
      }
    `};
`;
